import { getPaginationParams } from "@/shared/utils/pagination/pagination.ts";
import axiosClient from "@/shared/lib/axios/axios.ts";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@/shared/types/api.ts";
import { API_ROUTES } from "@/alerts/api/config.ts";
import { formatDateTime } from "@/shared/utils/date/format-datetime.ts";
import {
  Alert,
  AlertStatus,
  AlertDetail,
  AlertActivityDTO,
  AlertListItem,
  AlertMapResponse,
  GetAlertsInput,
  AlertOrdering,
  AlertScope,
} from "@/alerts/types/machine-alert.ts";

export interface IGetAlertsInput {
  device_mxid?: string;
  /**
   * Multi-device scope — sent as the comma-separated `device_mxids` query param
   * so one request covers a whole location instead of one request per machine.
   */
  device_mxids?: string[];
  start_time: Date;
  end_time: Date;
  page: number;
  itemsPerPage: number;
  type_in?: string[];
  status_in?: AlertStatus[];
  label_in?: string[];
  min_confidence?: number;
  max_confidence?: number;
  ordering?: AlertOrdering;
}

/** Newest-first unless a caller asks otherwise. */
const DEFAULT_ALERT_ORDERING: AlertOrdering = "-timestamp";

export interface ICreateAlertInput {
  origin: string;
  timestamp: string;
  type: string;
  event_uuid: string;
  status: AlertStatus;
  device_mxid: string;
}

export interface IUpdateAlertInput {
  status: AlertStatus;
}

export const getAlerts = (params: IGetAlertsInput) => {
  const { offset, limit } = getPaginationParams(params.page, params.itemsPerPage)

  return axiosClient.get<IGetAlertsInput, AxiosResponse<ApiResponse<Alert[]>>>(API_ROUTES.ALERTS, {
    params: {
      device_mxid: params?.device_mxid,
      device_mxids: csv(params.device_mxids),
      start_time: formatDateTime(params?.start_time, "start"),
      end_time: formatDateTime(params?.end_time, "end"),
      offset,
      limit,
      type_in: csv(params.type_in),
      status_in: csv(params.status_in),
      label_in: csv(params.label_in),
      min_confidence: params.min_confidence != null ? clamp01(params.min_confidence) : undefined,
      max_confidence: params.max_confidence != null ? clamp01(params.max_confidence) : undefined,
      ordering: params.ordering ?? DEFAULT_ALERT_ORDERING,
    }
  }).then(data => data.data);
}

/* ------------------------------------------------------------------ *
 * New Alerts API contract — GET /api/alerts/ (list)
 * ------------------------------------------------------------------ */

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** CSV param when the array has entries, else undefined (dropped from the query). */
const csv = (arr?: Array<string | number>) =>
  arr && arr.length > 0 ? arr.join(",") : undefined;

/** Emit exactly the one scope key that is present in the union. */
const scopeParams = (scope: AlertScope): Record<string, string | number> => {
  if ("device_mxid" in scope) return { device_mxid: scope.device_mxid };
  if ("device_mxids" in scope) return { device_mxids: scope.device_mxids.join(",") };
  if ("machine_ids" in scope) return { machine_ids: scope.machine_ids.join(",") };
  return { location_id: scope.location_id };
};

/**
 * Serialize a {@link GetAlertsInput} into query params for the new contract.
 * Sends exactly one scope key, a required time window, and only the optional
 * filters that are actually set. Confidence is clamped to [0,1]. `label_family`
 * is never emitted.
 */
export const buildAlertParams = (input: GetAlertsInput): Record<string, unknown> => {
  const f = input.filters ?? {};
  const params: Record<string, unknown> = {
    ...scopeParams(input.scope),
    start_time: formatDateTime(input.start_time, "start"),
    end_time: formatDateTime(input.end_time, "end"),
    type_in: csv(f.type_in),
    status_in: csv(f.status_in),
    label_in: csv(f.label_in),
    min_confidence: f.min_confidence != null ? clamp01(f.min_confidence) : undefined,
    max_confidence: f.max_confidence != null ? clamp01(f.max_confidence) : undefined,
    ordering: input.ordering,
    include_preview: input.include_preview || undefined,
    limit: input.limit,
    offset: input.offset,
  };
  // Drop undefined keys so they never hit the wire.
  Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
  return params;
};

/** GET /api/alerts/ — scoped, filtered, ordered, paginated alert list. */
export const getAlertsList = (input: GetAlertsInput) =>
  axiosClient
    .get<GetAlertsInput, AxiosResponse<ApiResponse<AlertListItem[]>>>(API_ROUTES.ALERTS, {
      params: buildAlertParams(input),
    })
    .then((res) => res.data);

/**
 * GET /api/alerts/map/ — same scope/filter/ordering contract as the list, minus
 * pagination. Reuses {@link buildAlertParams} so the map and list send byte-for-
 * byte identical `label_in`/`min_confidence`/`max_confidence` and stay in sync.
 * Returns a capped set with a `truncated` flag.
 */
export const getAlertsMap = (input: Omit<GetAlertsInput, "limit" | "offset">) =>
  axiosClient
    .get<GetAlertsInput, AxiosResponse<ApiResponse<AlertMapResponse>>>(API_ROUTES.ALERTS_MAP, {
      params: buildAlertParams(input),
    })
    .then((res) => res.data);

/**
 * Get alert by ID with images and preview.
 * @param id - Alert ID
 * @param opts.include_images - default true; pass false for a lightweight
 *   preview-only fetch (the backend still returns `preview`).
 */
export const getAlertById = (
  id: string | number,
  opts?: { include_images?: boolean },
) => {
  const params =
    opts?.include_images === false ? { include_images: false } : undefined;
  return axiosClient
    .get<any, AxiosResponse<ApiResponse<AlertDetail>>>(`${API_ROUTES.ALERTS}${id}/`, {
      params,
    })
    .then(data => data.data);
}

/**
 * GET /api/alerts/{id}/activities/ — the workflow timeline for one alert
 * (create / resolve / ignore / reopen / note), oldest → newest as returned.
 */
export const getAlertActivities = (id: string | number) =>
  axiosClient
    .get<unknown, AxiosResponse<ApiResponse<AlertActivityDTO[]>>>(
      `${API_ROUTES.ALERTS}${id}/activities/`,
    )
    .then((res) => res.data);

/*
 * Removed for the release-candidate contract: operator writes and live state no
 * longer live in the machines layer. The Alerts workspace owns all writes via
 * `POST /api/alerts/{id}/actions/` (see actions.requests.ts) and
 * Firestore-hinted queue polling (live.requests.ts). The
 * `/kpis/`, `/shifts/`, `/resolve/`, `/ignore/`, `/reopen/`, `/notes/`, and
 * `/live/state/` endpoints no longer exist in the backend contract.
 */

export const createAlert = (data: ICreateAlertInput) => {
  return axiosClient.post<any, AxiosResponse<ApiResponse<Alert>>>(
    API_ROUTES.ALERTS,
    {
      origin: data.origin,
      timestamp: data.timestamp,
      type: data.type,
      event_uuid: data.event_uuid,
      status: data.status,
      device_mxid: data.device_mxid,
    }
  ).then(data => data.data);
}

export const updateAlert = (id: string, data: IUpdateAlertInput) => {
  return axiosClient.patch<any, AxiosResponse<ApiResponse<Alert>>>(
    `${API_ROUTES.ALERTS}${id}/`,
    {
      status: data.status,
    }
  ).then(data => data.data);
}

export const deleteAlert = (id: string) => {
  return axiosClient.delete<any, AxiosResponse<ApiResponse<void>>>(
    `${API_ROUTES.ALERTS}${id}/`
  ).then(data => data.data);
}
